import { useEffect, useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuth, useLogin, useLogout, postAuthPath } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AuthCard } from '@/components/portal/auth-card'
import { Wordmark } from '@/components/portal/wordmark'

export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, isLoading } = useAuth()
  const login = useLogin()
  const logout = useLogout()
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const resetSuccess = searchParams.get('reset') === 'success'

  useEffect(() => {
    if (!resetSuccess) return
    const timer = window.setTimeout(() => {
      setSearchParams({}, { replace: true })
    }, 8000)
    return () => window.clearTimeout(timer)
  }, [resetSuccess, setSearchParams])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    try {
      const { user: loggedIn } = await login.mutateAsync({ identifier, password })
      navigate(postAuthPath(loggedIn), { replace: true })
    } catch {
      // error handled by login.error
    }
  }

  async function handleSignOut() {
    // An already-expired session makes logout fail; the cache is cleared either way
    await logout.mutateAsync().catch(() => undefined)
  }

  if (isLoading) {
    return (
      <AuthCard>
        <Wordmark portal="Estate Portal" className="mb-8" />
        <p className="text-center font-sans text-sm text-muted-foreground">Loading…</p>
      </AuthCard>
    )
  }

  if (user) {
    const displayEmail = user.email ?? user.phone
    return (
      <AuthCard>
        <Wordmark portal="Estate Portal" className="mb-8" />
        <h2 className="mb-2 text-center font-serif text-2xl font-medium text-ink">
          Already signed in
        </h2>
        <p className="mb-6 text-center font-sans text-sm text-muted-foreground">
          You are signed in as <strong className="text-ink">{user.name}</strong>
          {displayEmail ? (
            <>
              {' '}
              (<span className="text-ink">{displayEmail}</span>)
            </>
          ) : null}
          .
        </p>
        <div className="space-y-3">
          <Button
            type="button"
            className="w-full"
            size="lg"
            onClick={() => navigate(postAuthPath(user), { replace: true })}
          >
            Continue to portal
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            size="lg"
            loading={logout.isPending}
            onClick={() => void handleSignOut()}
          >
            Sign out
          </Button>
        </div>
      </AuthCard>
    )
  }

  return (
    <AuthCard>
      <Wordmark portal="Estate Portal" className="mb-8" />

      <form onSubmit={handleSubmit} className="space-y-5">
        {resetSuccess && (
          <p className="rounded border border-status-paid-bg bg-status-paid-bg/40 px-3 py-2 font-sans text-sm text-status-paid-text">
            Your password has been updated. Sign in with your new password.
          </p>
        )}

        <Input
          label="Email or phone number"
          type="text"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          autoComplete="username"
          hint="Phone format: +254XXXXXXXXX"
          required
        />
        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />

        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            className="font-sans text-sm text-primary transition-colors hover:text-green-lt"
          >
            Forgot password?
          </Link>
        </div>

        {login.error && (
          <p
            className="rounded border border-destructive/30 bg-status-overdue-bg px-3 py-2 font-sans text-sm text-destructive"
            role="alert"
          >
            {login.error.message}
          </p>
        )}

        <Button type="submit" className="w-full" size="lg" loading={login.isPending}>
          Sign in
        </Button>
      </form>

      <p className="mt-6 text-center font-sans text-xs text-muted-foreground">
        Access is by invitation only. Invited members must use their invite link to set a password
        before signing in.
      </p>
    </AuthCard>
  )
}
